import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DatumVeld, geldigeDatum, minDatum, teVroeg, vandaagNL, TE_VROEG } from "@/components/DatumVeld";
import { Plus, X, CalendarClock, Trash2, Lock, MapPin } from "lucide-react";
import { format, parseISO, addDays } from "date-fns";
import { nl } from "date-fns/locale";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const EIGENAAR_OPTS = [
  { value: "bedrijfsleider", label: "Bedrijfsleider" },
  { value: "om", label: "OM" },
  { value: "beide", label: "Beide" },
  { value: "afdeling", label: "Afdeling" },   // v19 A3
];

const WEEKDAGEN = [
  { value: 1, label: "ma" }, { value: 2, label: "di" }, { value: 3, label: "wo" },
  { value: 4, label: "do" }, { value: 5, label: "vr" }, { value: 6, label: "za" }, { value: 0, label: "zo" },
];
const ALL_DAYS = WEEKDAGEN.map((w) => w.value);

// Generate `count` upcoming dates (yyyy-MM-dd) for the given JS weekday (0=zo..6=za), starting today/forward.
function genWeekdayDates(weekday, count) {
  const out = [];
  let d = new Date(); d.setHours(0, 0, 0, 0);
  let guard = 0;
  while (d.getDay() !== weekday && guard < 7) { d = addDays(d, 1); guard++; }
  for (let i = 0; i < count; i++) {
    out.push(format(d, "yyyy-MM-dd"));
    d = addDays(d, 7);
  }
  return out;
}

// For multiple weekdays: the next N occurrences of EACH selected day, merged, deduped, sorted.
function genMultiWeekdayDates(days, count) {
  const set = new Set();
  for (const day of days) for (const ds of genWeekdayDates(Number(day), Number(count))) set.add(ds);
  return [...set].sort((a, b) => a.localeCompare(b));
}

let _blockSeq = 0;
let _cpSeq = 0;
// Lokale sleutel per deelpunt: het server-id als dat er is, anders een tijdelijke (nooit meegestuurd).
const newCp = (date) => ({ _k: `cp-new-${++_cpSeq}`, date, wat: "", done: false, note: "" });
const cpKey = (c) => c.id || c._k;
const byDate = (a, c) => a.date.localeCompare(c.date);
// Stabiele lege default: een nieuwe [] per render in effect-deps gaf een oneindige render-lus (v9-R1 F1).
const GEEN = [];
function LosseDatum({ testid, onAdd }) {
  const [v, setV] = useState("");
  const vroeg = teVroeg(v, "");
  const ok = geldigeDatum(v) && !vroeg;
  return (
    <div>
      <label className="block text-sm font-medium text-blue-700 mb-1 flex items-center gap-1.5"><Plus className="h-4 w-4" /> Losse datum toevoegen</label>
      <div className="flex items-stretch gap-2">
        <DatumVeld testid={testid} value={v} onChange={setV} min={vandaagNL()} aria-label="Losse datum toevoegen" />
        <button type="button" data-testid={`${testid}-toevoegen`} disabled={!ok} onClick={() => { onAdd(v); setV(""); }}
          className="shrink-0 min-h-[44px] px-4 rounded-md border border-blue-600 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-40 disabled:hover:bg-transparent">Toevoegen</button>
      </div>
      {v && !ok && <p data-testid={`${testid}-fout`} className="text-xs font-semibold text-rose-600 mt-1">{vroeg ? TE_VROEG : "Kies een datum tussen het jaar 2000 en 2100."}</p>}
    </div>
  );
}

const GEEN_OBJ = {};
const emptyBlock = () => ({ _id: `blk-${++_blockSeq}`, wat: "", eigenaar: "beide", afdeling: "", werkbon: "", mode: "single", deadline: "", checkpoints: [], genDays: [4], genCount: 3, cpDirty: false });

export default function AfspraakDialog({ open, onOpenChange, week, locaties, generalBuckets = GEEN, oms = GEEN, internKandidaten = GEEN, blContact = GEEN_OBJ, afdelingen = GEEN, defaultAfdeling = "", defaultLocatie, defaultWat, onCreated, afspraak }) {
  const isEdit = !!afspraak;
  const oudeDeadline = (isEdit && afspraak.deadline) || "";
  const labelGewijzigd = useRef(false);
  const { user } = useAuth();
  const [shared, setShared] = useState({ locatie: defaultLocatie || "", bedrijfsleider: "" });
  const [blocks, setBlocks] = useState([emptyBlock()]);
  const [saving, setSaving] = useState(false);
  const [opvolger, setOpvolger] = useState("");
  const [opvolgerDirty, setOpvolgerDirty] = useState(false);
  const eigenDefault = oms.includes(user?.username) ? user.username : "";
  const [soort, setSoort] = useState("locatie");
  const [betrokkenen, setBetrokkenen] = useState([]);
  const [eigenaarPersoon, setEigenaarPersoon] = useState("");
  const [betrokkenenDirty, setBetrokkenenDirty] = useState(false);
  const [bevestigIntern, setBevestigIntern] = useState(false);
  const intern = soort === "intern";
  // Een OM mag toevoegen, nooit iemand eruit halen (besluit 2026-09-29); oversight wel.
  const vastBetrokken = isEdit && user?.role !== "oversight" ? (afspraak.betrokkenen || GEEN) : GEEN;
  const ikKandidaat = internKandidaten.includes(user?.username);

  useEffect(() => {
    if (!open) return;
    setBetrokkenenDirty(false);
    if (afspraak) {
      setSoort(afspraak.soort === "intern" ? "intern" : "locatie");
      setBetrokkenen(afspraak.betrokkenen || []);
      setEigenaarPersoon(afspraak.eigenaarPersoon || "");
    } else {
      const ik = ikKandidaat ? [user.username] : [];
      setSoort("locatie"); setBetrokkenen(ik); setEigenaarPersoon(ik[0] || "");
    }
    // v10-B6: op INHOUD (boolean), nooit op array-identiteit — een inline [] kan zo nooit een lus starten.
  }, [open, afspraak, ikKandidaat, user?.username]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleBetrokkene = (u) => {
    if (vastBetrokken.includes(u)) return;
    setBetrokkenenDirty(true);
    setBetrokkenen((bs) => {
      const next = bs.includes(u) ? bs.filter((b) => b !== u) : [...bs, u];
      if (!next.includes(eigenaarPersoon)) setEigenaarPersoon("");
      return next;
    });
  };
  const kiesEigenaar = (v) => { setBetrokkenenDirty(true); setEigenaarPersoon(v); };
  // Naar Intern: alleen het eerste blok blijft; staat er tekst in de andere, dan eerst bevestigen.
  const naarIntern = () => {
    if (blocks.slice(1).some((b) => b.wat.trim())) { setBevestigIntern(true); return; }
    setBlocks((bs) => bs.slice(0, 1));
    setSoort("intern");
  };
  const bevestigNaarIntern = () => { setBlocks((bs) => bs.slice(0, 1)); setSoort("intern"); setBevestigIntern(false); };
  const kandidaten = [...new Set([...internKandidaten, ...betrokkenen])];

  useEffect(() => {
    if (!open) return;
    setOpvolgerDirty(false);
    labelGewijzigd.current = false;
    setOpvolger(afspraak ? (afspraak.opvolgerEffectief || "") : eigenDefault);
  }, [open, afspraak, eigenDefault]);

  useEffect(() => {
    if (!open) return;
    if (afspraak) {
      setShared({ locatie: afspraak.locatie || "", bedrijfsleider: afspraak.bedrijfsleider || "" });
      const cps = Array.isArray(afspraak.checkpoints) ? afspraak.checkpoints : [];
      setBlocks([{
        _id: `blk-${++_blockSeq}`,
        wat: afspraak.wat || "",
        eigenaar: afspraak.eigenaar || "beide",
        afdeling: afspraak.afdeling || "", werkbon: afspraak.werkbon || "",
        mode: cps.length > 0 ? "multi" : "single",
        deadline: afspraak.deadline || "",
        checkpoints: cps,
        genDays: [4], genCount: 3, cpDirty: false,
      }]);
    } else {
      const auto = blContactRef.current[defaultLocatie] || "";
      autoBlRef.current = auto;
      setShared({ locatie: defaultLocatie || "", bedrijfsleider: auto });
      setBlocks([{ ...emptyBlock(), wat: defaultWat || "", ...(defaultAfdeling ? { eigenaar: "afdeling", afdeling: defaultAfdeling } : {}) }]);
    }
  }, [open, defaultLocatie, defaultWat, afspraak, defaultAfdeling]);

  // v18 C4: bij een nieuwe afspraak volgt "Bedrijfsleider" de gekozen locatie, zolang hij leeg is of nog de automatische naam heeft.
  const blContactRef = useRef(blContact);
  blContactRef.current = blContact;
  const autoBlRef = useRef("");
  // v18-R1 R7: de keuze valt buiten de state-updater (die mag React dubbel draaien); de ref ook.
  const setSharedField = (k, v) => {
    if (k !== "locatie" || isEdit || (shared.bedrijfsleider.trim() && shared.bedrijfsleider !== autoBlRef.current)) {
      setShared((s) => ({ ...s, [k]: v }));
      return;
    }
    const auto = blContact[v] || "";
    autoBlRef.current = auto;
    setShared((s) => ({ ...s, locatie: v, bedrijfsleider: auto }));
  };
  const updateBlock = (i, patch) => setBlocks((bs) => bs.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const addBlock = () => setBlocks((bs) => [...bs, emptyBlock()]);
  const removeBlock = (i) => setBlocks((bs) => bs.filter((_, idx) => idx !== i));

  // Dezelfde datum mag vaker: meerdere deelpunten kunnen op dezelfde dag af moeten.
  const addCheckpoint = (i, dateStr) => {
    if (!dateStr) return;
    setBlocks((bs) => bs.map((b, idx) => (idx !== i ? b
      : { ...b, cpDirty: true, checkpoints: [...b.checkpoints, newCp(dateStr)].sort(byDate) })));
  };
  const removeCheckpoint = (i, key) =>
    setBlocks((bs) => bs.map((b, idx) => (idx === i ? { ...b, cpDirty: true, checkpoints: b.checkpoints.filter((c) => cpKey(c) !== key) } : b)));
  const setCheckpointWat = (i, key, wat) =>
    setBlocks((bs) => bs.map((b, idx) => (idx === i ? { ...b, cpDirty: true, checkpoints: b.checkpoints.map((c) => (cpKey(c) === key ? { ...c, wat } : c)) } : b)));
  const validCount = (v) => Number.isInteger(Number(v)) && Number(v) >= 1 && Number(v) <= 12;
  const generateCheckpoints = (i) => {
    setBlocks((bs) => bs.map((b, idx) => {
      if (idx !== i) return b;
      if (!b.genDays.length || !validCount(b.genCount)) return b;
      const dates = genMultiWeekdayDates(b.genDays, Math.floor(Number(b.genCount)));
      const merged = [...b.checkpoints];
      for (const d of dates) if (!merged.some((c) => c.date === d)) merged.push(newCp(d));
      return { ...b, cpDirty: true, checkpoints: merged.sort(byDate) };
    }));
  };
  const toggleGenDay = (i, dayVal) =>
    setBlocks((bs) => bs.map((b, idx) => {
      if (idx !== i) return b;
      const has = b.genDays.includes(dayVal);
      return { ...b, genDays: has ? b.genDays.filter((d) => d !== dayVal) : [...b.genDays, dayVal] };
    }));
  const setAllGenDays = (i) =>
    setBlocks((bs) => bs.map((b, idx) => (idx === i ? { ...b, genDays: b.genDays.length === ALL_DAYS.length ? [] : [...ALL_DAYS] } : b)));

  const buildPayload = (b, includeCheckpoints) => {
    const p = intern ? {
      week: isEdit ? afspraak.week : week, soort: "intern",
      // Bij bewerken alleen meesturen als hier gewijzigd: een verouderde lijst zet nooit iemand terug.
      ...(!isEdit || betrokkenenDirty ? { betrokkenen, eigenaarPersoon } : {}),
      // v20 C: locatie is bij intern alleen een label ("" = geen); zichtbaarheid blijft intern
      // v20-R1 R10: bij bewerken alleen meesturen als het label in dit venster is gewijzigd (null = niet aanraken)
      locatie: isEdit && !labelGewijzigd.current ? null : (locaties.includes(shared.locatie) ? shared.locatie : ""), bedrijfsleider: "", wat: b.wat, eigenaar: "",
      deadline: b.mode === "multi" ? null : (b.deadline || null),
    } : {
      week: isEdit ? afspraak.week : week,  // bewerken behoudt de oorspronkelijke week (#5)
      locatie: shared.locatie || null,
      bedrijfsleider: shared.bedrijfsleider,
      wat: b.wat,
      // v19-R1 R8: bij bewerken eigenaar en afdeling alleen als hier gewijzigd; een verouderd formulier zet niets terug.
      ...(!isEdit || b.eigenaar !== afspraak.eigenaar || (b.eigenaar === "afdeling" && (b.afdeling || "") !== (afspraak.afdeling || ""))
        ? { eigenaar: b.eigenaar, ...(b.eigenaar === "afdeling" ? { afdeling: b.afdeling || null } : {}) } : {}),
      // v19-A-R R3: bij bewerken alleen als hier gewijzigd; een verouderd formulier wist nooit de werkbon van de afdeling.
      ...(!isEdit || (b.werkbon || "") !== (afspraak.werkbon || "") ? { werkbon: b.werkbon || null } : {}),
      deadline: b.mode === "multi" ? null : (b.deadline || null),
    };
    // Opvolger: bij aanmaken altijd; bij bewerken alleen als hij hier gewijzigd is (smalle PATCH blijft leidend).
    if (!intern && (!isEdit || opvolgerDirty)) p.opvolger = opvolger || null;
    // checkpoints alleen meesturen als expliciet gewijzigd (#3): voorkomt overschrijven van een
    // gelijktijdige PATCH /checkpoints met een stale kopie.
    if (includeCheckpoints) {
      p.checkpoints = b.mode === "multi"
        ? b.checkpoints.map((c) => ({ ...(c.id ? { id: c.id } : {}), date: c.date, wat: c.wat || "", done: !!c.done, note: c.note || "" }))
        : [];
    }
    return p;
  };

  const save = async () => {
    if (intern && betrokkenen.length < 2) { toast.error("Kies minstens twee betrokkenen."); return; }
    if (intern && !betrokkenen.includes(eigenaarPersoon)) { toast.error("Kies één eigenaar uit de betrokkenen."); return; }
    if (!intern && blocks.some((b) => b.eigenaar === "afdeling" && !b.afdeling)) { toast.error("Kies bij welke afdeling de afspraak ligt."); return; }
    if (blocks.some((x) => x.mode !== "multi" && x.deadline && !geldigeDatum(x.deadline))) { toast.error("Kies een deadline tussen het jaar 2000 en 2100."); return; }
    if (blocks.some((x) => x.mode !== "multi" && teVroeg(x.deadline, oudeDeadline))) { toast.error(`Deadline: ${TE_VROEG}`); return; }
    if (isEdit) {
      const b = blocks[0];
      if (!b.wat.trim()) { toast.error("Vul in wat er afgesproken is."); return; }
      if (b.mode === "multi" && b.checkpoints.length === 0) { toast.error("Voeg minstens één peilmoment toe of kies één deadline."); return; }
      setSaving(true);
      try {
        // In edit: checkpoints alleen meesturen als de gebruiker peilmomenten/modus wijzigde (b.cpDirty).
        const payload = { ...buildPayload(b, b.cpDirty), status: afspraak.status };
        const res = await api.put(`/afspraken/${afspraak.id}`, payload);
        toast.success("Afspraak bijgewerkt");
        onCreated && onCreated(res.data);
        onOpenChange(false);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Opslaan mislukt");
      } finally { setSaving(false); }
      return;
    }

    // Create: one or more afspraken sharing locatie + bedrijfsleider.
    const filled = (intern ? blocks.slice(0, 1) : blocks).filter((b) => b.wat.trim());
    if (filled.length === 0) { toast.error("Vul minstens één afspraak in."); return; }
    for (const b of filled) {
      if (b.mode === "multi" && b.checkpoints.length === 0) {
        toast.error(`"${b.wat.trim().slice(0, 30)}…" mist peilmomenten — kies een deadline of voeg peilmomenten toe.`);
        return;
      }
    }
    setSaving(true);
    let created = 0;
    let last;
    let failed = false;
    let failMsg = "";
    try {
      for (const b of filled) {
        try {
          const res = await api.post("/afspraken", buildPayload(b, true));
          last = res.data;
          created += 1;
        } catch (e) {
          failed = true;
          failMsg = e?.response?.data?.detail || "Opslaan mislukt";
          break;
        }
      }
    } finally {
      setSaving(false);
    }
    if (created > 0) {
      // Sluit + ververs zodat opnieuw indienen geen duplicaten maakt.
      if (failed) toast.error(`${created} van ${filled.length} toegevoegd — de rest mislukte (${failMsg})`);
      else toast.success(created === 1 ? "Afspraak toegevoegd" : `${created} afspraken toegevoegd`);
      onCreated && onCreated(last);
      onOpenChange(false);
    } else {
      toast.error(failMsg || "Opslaan mislukt");
    }
  };

  const inputCls =
    "w-full px-3 py-2.5 rounded-md border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm text-zinc-900";
  const T = (base, i) => (i === 0 ? base : `${base}-${i}`);

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-w-[calc(100vw-1.5rem)] max-h-[90vh] overflow-y-auto" data-testid="afspraak-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Afspraak bewerken" : "Nieuwe afspraak"}</DialogTitle>
          <DialogDescription>
            {intern ? "Een interne afspraak is alleen zichtbaar voor de betrokkenen en oversight. Eén eigenaar, nooit \"we\"."
              : isEdit ? "Pas vast wat er met de locatie is afgesproken en wie eigenaar is." : "Leg vast wat er met de locatie is afgesproken. Je kunt meerdere afspraken voor dezelfde locatie tegelijk toevoegen."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5 py-1">
          {!isEdit && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 rounded-md border border-zinc-300 bg-white p-0.5" data-testid="afspraak-soort">
              <button type="button" data-testid="afspraak-soort-locatie" onClick={() => setSoort("locatie")}
                className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm text-xs font-semibold transition-colors ${!intern ? "bg-zinc-950 text-white" : "text-zinc-500 hover:bg-zinc-100"}`}>
                <MapPin className="h-3.5 w-3.5" /> Met een locatie
              </button>
              <button type="button" data-testid="afspraak-soort-intern" onClick={naarIntern}
                className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm text-xs font-semibold transition-colors ${intern ? "bg-zinc-950 text-white" : "text-zinc-500 hover:bg-zinc-100"}`}>
                <Lock className="h-3.5 w-3.5 shrink-0" /> Intern (tussen OM's en/of Milos en Johannes)
              </button>
            </div>
          )}
          {intern ? (
            <>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Betrokkenen</label>
                <div className="flex flex-wrap gap-2" data-testid="afspraak-betrokkenen">
                  {kandidaten.map((u) => (
                    <label key={u} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-sm capitalize cursor-pointer select-none transition-colors ${betrokkenen.includes(u) ? "border-blue-600 bg-blue-50 text-zinc-900" : "border-zinc-300 text-zinc-500 hover:border-zinc-400"}`}>
                      <input type="checkbox" data-testid={`afspraak-betrokkene-${u}`} checked={betrokkenen.includes(u)} disabled={vastBetrokken.includes(u)} onChange={() => toggleBetrokkene(u)} className="h-4 w-4 accent-blue-600 disabled:opacity-60" />
                      {u}
                    </label>
                  ))}
                </div>
                {vastBetrokken.length > 0 && <p data-testid="afspraak-betrokkenen-hint" className="mt-1.5 text-xs text-zinc-500">Iemand eruit halen? Vraag Milos of Johannes.</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Over locatie <span className="text-zinc-400 font-normal">— alleen een label, de locatie ziet niets</span></label>
                <select data-testid="afspraak-intern-locatie" value={locaties.includes(shared.locatie) ? shared.locatie : ""} onChange={(e) => { labelGewijzigd.current = true; setShared((x) => ({ ...x, locatie: e.target.value })); }} className={inputCls}>
                  <option value="">Geen locatie</option>
                  {locaties.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Eigenaar <span className="text-zinc-400 font-normal">— één naam, nooit "we"</span></label>
                <select data-testid="afspraak-eigenaar-persoon" value={eigenaarPersoon} onChange={(e) => kiesEigenaar(e.target.value)} className={`${inputCls} capitalize`}>
                  <option value="">Kies de eigenaar</option>
                  {betrokkenen.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </>
          ) : (<>
          {/* Gedeeld: locatie + bedrijfsleider */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Locatie (optioneel)</label>
            <select data-testid="afspraak-locatie" value={shared.locatie} onChange={(e) => setSharedField("locatie", e.target.value)} className={inputCls}>
              <option value="">— Geen / algemeen —</option>
              {generalBuckets.length > 0 && (
                <optgroup label="Algemeen">
                  {generalBuckets.map((g) => <option key={g} value={g}>{g}</option>)}
                </optgroup>
              )}
              <optgroup label="Locaties">
                {locaties.map((l) => <option key={l} value={l}>{l}</option>)}
              </optgroup>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Bedrijfsleider</label>
            <input data-testid="afspraak-bedrijfsleider" value={shared.bedrijfsleider} onChange={(e) => setSharedField("bedrijfsleider", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Opvolger <span className="text-zinc-400 font-normal">— welke OM pakt dit op</span></label>
            <select data-testid="afspraak-opvolger" value={opvolger} onChange={(e) => { setOpvolger(e.target.value); setOpvolgerDirty(true); }} className={inputCls}>
              <option value="">Geen opvolger</option>
              {oms.map((o) => <option key={o} value={o}>{o}</option>)}
              {opvolger && !oms.includes(opvolger) && <option value={opvolger}>{opvolger} (uit dienst)</option>}
            </select>
          </div>
          </>)}

          {(intern ? blocks.slice(0, 1) : blocks).map((b, i) => (
            <div key={b._id} data-testid={`afspraak-block-${i}`} className={`rounded-md border p-3 space-y-3.5 ${blocks.length > 1 ? "border-zinc-300 bg-zinc-50/40" : "border-transparent p-0"}`}>
              {!isEdit && !intern && blocks.length > 1 && (
                <div className="flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-wider font-bold text-zinc-500">Afspraak {i + 1}</span>
                  <button type="button" data-testid={`remove-afspraak-block-${i}`} onClick={() => removeBlock(i)} className="flex items-center gap-1 text-xs text-zinc-400 hover:text-rose-600">
                    <Trash2 className="h-3.5 w-3.5" /> Verwijderen
                  </button>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Wat is afgesproken?</label>
                <textarea data-testid={T("afspraak-wat", i)} rows={2} value={b.wat} onChange={(e) => updateBlock(i, { wat: e.target.value })} className={inputCls} />
              </div>

              {!intern && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Eigenaar</label>
                  <select data-testid={T("afspraak-eigenaar", i)} value={b.eigenaar} onChange={(e) => updateBlock(i, { eigenaar: e.target.value })} className={inputCls}>
                    {EIGENAAR_OPTS.filter((o) => o.value !== "afdeling" || afdelingen.length > 0).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                {b.eigenaar === "afdeling" && <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Afdeling</label>
                  <select data-testid={T("afspraak-afdeling", i)} value={b.afdeling} onChange={(e) => updateBlock(i, { afdeling: e.target.value })} className={inputCls}>
                    <option value="">Kies een afdeling</option>
                    {afdelingen.filter((a) => a.actief !== false || (isEdit && a.id === afspraak?.afdeling)).map((a) => (
                      <option key={a.id} value={a.id}>{a.naam}{a.actief === false ? " (niet actief)" : ""}</option>
                    ))}
                    {isEdit && afspraak?.afdeling && !afdelingen.some((a) => a.id === afspraak.afdeling) && <option value={afspraak.afdeling}>{afspraak.afdelingNaam || afspraak.afdeling} (niet actief)</option>}
                  </select>
                </div>}
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Werkbon <span className="text-zinc-400 font-normal">(optioneel)</span></label>
                  <input data-testid={T("afspraak-werkbon", i)} maxLength={40} value={b.werkbon} onChange={(e) => updateBlock(i, { werkbon: e.target.value })} className={inputCls} placeholder="bijv. TD-2214" />
                </div>
              </div>}

              <div className="rounded-md border border-zinc-200 p-3 space-y-3">
                <div className="flex items-center gap-1 rounded-md border border-zinc-300 bg-white p-0.5 w-fit" data-testid={T("afspraak-deadline-mode", i)}>
                  <button type="button" data-testid={T("mode-single", i)} onClick={() => updateBlock(i, { mode: "single", cpDirty: true })}
                    className={`px-3 py-1.5 rounded-sm text-xs font-semibold transition-colors ${b.mode === "single" ? "bg-zinc-950 text-white" : "text-zinc-500 hover:bg-zinc-100"}`}>
                    Eén deadline
                  </button>
                  <button type="button" data-testid={T("mode-multi", i)} onClick={() => updateBlock(i, { mode: "multi", cpDirty: true })}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs font-semibold transition-colors ${b.mode === "multi" ? "bg-zinc-950 text-white" : "text-zinc-500 hover:bg-zinc-100"}`}>
                    <CalendarClock className="h-3.5 w-3.5" /> Meerdere peilmomenten
                  </button>
                </div>

                {b.mode === "single" ? (
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-1">Deadline</label>
                    <DatumVeld testid={T("afspraak-deadline", i)} value={b.deadline} onChange={(v) => updateBlock(i, { deadline: v })} min={minDatum(oudeDeadline)} aria-label="Deadline" />
                    {b.deadline && (!geldigeDatum(b.deadline) || teVroeg(b.deadline, oudeDeadline)) && <p data-testid={T("afspraak-deadline-fout", i)} className="text-xs font-semibold text-rose-600 mt-1">{geldigeDatum(b.deadline) ? TE_VROEG : "Kies een datum tussen het jaar 2000 en 2100."}</p>}
                  </div>
                ) : (
                  <div className="space-y-3" data-testid={T("afspraak-checkpoints-editor", i)}>
                    {/* Snelle helper — multi-dag */}
                    <div className="rounded-md bg-zinc-50 border border-zinc-200 p-2.5">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">Peilmomenten · snel toevoegen — elke …</span>
                        <button type="button" data-testid={T("gen-all-days", i)} onClick={() => setAllGenDays(i)} className="text-[11px] font-semibold text-blue-700 hover:text-blue-900">
                          {b.genDays.length === ALL_DAYS.length ? "Wis dagen" : "Alle dagen"}
                        </button>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 mb-2">
                        {WEEKDAGEN.map((w) => {
                          const on = b.genDays.includes(w.value);
                          return (
                            <button key={w.value} type="button" data-testid={i === 0 ? `gen-weekday-${w.value}` : `gen-weekday-${i}-${w.value}`}
                              onClick={() => toggleGenDay(i, w.value)}
                              className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize border transition-colors ${on ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-zinc-300 text-zinc-500 hover:border-zinc-400"}`}>
                              {w.label}
                            </button>
                          );
                        })}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <input data-testid={T("gen-count", i)} type="number" min={1} max={12} value={b.genCount} onChange={(e) => updateBlock(i, { genCount: e.target.value })}
                          className="w-16 px-2 py-1.5 rounded-md border border-zinc-300 bg-white text-sm" />
                        <span className="text-sm text-zinc-500">keer</span>
                        <button type="button" data-testid={T("gen-generate", i)} onClick={() => generateCheckpoints(i)} disabled={!b.genDays.length || !validCount(b.genCount)}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 disabled:opacity-50">
                          <Plus className="h-3.5 w-3.5" /> Genereer
                        </button>
                      </div>
                    </div>

                    {b.checkpoints.length === 0 ? (
                      <p className="text-xs text-zinc-400">Nog geen peilmomenten. Kies dagen + aantal en genereer, of voeg een losse datum toe.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {b.checkpoints.map((c, j) => (
                          <div key={cpKey(c)} data-testid={i === 0 ? `cp-row-${j}` : `cp-row-${i}-${j}`} data-date={c.date} className="rounded-md border border-zinc-200 px-3 py-1.5 space-y-1">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm text-zinc-700">{format(parseISO(c.date), "EEEE d MMM yyyy", { locale: nl })}</span>
                              <button type="button" aria-label="Deelpunt verwijderen" data-testid={i === 0 ? `cp-remove-${j}` : `cp-remove-${i}-${j}`} onClick={() => removeCheckpoint(i, cpKey(c))} className="text-zinc-400 hover:text-rose-600">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                            <input data-testid={i === 0 ? `cp-wat-${j}` : `cp-wat-${i}-${j}`} value={c.wat || ""} maxLength={200} placeholder="Wat moet er dan af? (optioneel)"
                              onChange={(e) => setCheckpointWat(i, cpKey(c), e.target.value)}
                              className="w-full text-xs px-2 py-1.5 rounded border border-zinc-200 focus:outline-none focus:ring-1 focus:ring-blue-600 text-zinc-800" />
                          </div>
                        ))}
                      </div>
                    )}

                    <LosseDatum testid={T("cp-add-date", i)} onAdd={(v) => addCheckpoint(i, v)} />
                  </div>
                )}
              </div>
            </div>
          ))}

          {!isEdit && !intern && (
            <button type="button" data-testid="add-afspraak-block" onClick={addBlock}
              className="flex items-center gap-1.5 w-full justify-center px-3 py-2.5 rounded-md border border-dashed border-zinc-300 text-sm font-semibold text-zinc-500 hover:border-blue-400 hover:text-blue-700 transition-colors">
              <Plus className="h-4 w-4" /> Nog een afspraak voor deze locatie
            </button>
          )}
        </div>

        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="w-full sm:w-auto px-4 py-2.5 rounded-md text-sm font-medium text-zinc-500 hover:bg-zinc-100">Annuleren</button>
          <button data-testid="afspraak-save" onClick={save} disabled={saving} className="w-full sm:w-auto px-4 py-2.5 rounded-md text-sm font-semibold bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2">
            {saving ? "Bezig…" : "Opslaan"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
      <AlertDialog open={bevestigIntern} onOpenChange={setBevestigIntern}>
        <AlertDialogContent data-testid="intern-bevestig-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Naar een interne afspraak?</AlertDialogTitle>
            <AlertDialogDescription>De andere afspraken in dit venster vervallen. Doorgaan?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="intern-bevestig-annuleren">Annuleren</AlertDialogCancel>
            <AlertDialogAction data-testid="intern-bevestig-doorgaan" onClick={bevestigNaarIntern}>Doorgaan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
